import { forwardRef, type Ref, type ReactNode } from 'react';
import { useAppStore, type ThemeMode } from '@repletoryreach/sdk-utils/store';
import { SegmentedControl, type SegmentedControlProps } from '../inputs/SegmentedControl/SegmentedControl.js';

export interface ThemeToggleProps extends Omit<SegmentedControlProps, 'data' | 'value' | 'onChange'> {
  /** Hide labels and show icon-only buttons (compact header use). */
  iconOnly?: boolean;
  /** Override the displayed labels. */
  labels?: Partial<Record<ThemeMode, string>>;
}

const DEFAULT_LABELS: Record<ThemeMode, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System'
};

const ICONS: Record<ThemeMode, ReactNode> = {
  light: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden>
      <circle cx="7" cy="7" r="2.6" />
      <path d="M7 1.5v1.4M7 11.1v1.4M1.5 7h1.4M11.1 7h1.4M2.8 2.8l1 1M10.2 10.2l1 1M2.8 11.2l1-1M10.2 3.8l1-1" />
    </svg>
  ),
  dark: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden>
      <path d="M11.4 8.4a5 5 0 11-5.8-5.8 4 4 0 005.8 5.8z" />
    </svg>
  ),
  system: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="1.5" y="2.5" width="11" height="7.5" rx="1" />
      <path d="M5 12.5h4M7 10v2.5" />
    </svg>
  )
};

/**
 * Theme-mode picker bound to sdk-utils' `useAppStore`. Renders three options
 * (Light · Dark · System) on a `SegmentedControl`; writes via `setThemeMode`,
 * which UIProvider reads and turns into `[data-theme]` + CSS-var updates.
 *
 *   <ThemeToggle />            // labels + icons
 *   <ThemeToggle iconOnly />   // icons only (compact header)
 */
export const ThemeToggle = forwardRef(function ThemeToggle(
  { iconOnly, labels, size, fullWidth, ...rest }: ThemeToggleProps,
  _ref: Ref<HTMLDivElement>
): ReactNode {
  const value = useAppStore((s) => s.themeMode);
  const setThemeMode = useAppStore((s) => s.setThemeMode);

  const merged = { ...DEFAULT_LABELS, ...labels };
  const data = (['light', 'dark', 'system'] as const).map((mode) => ({
    value: mode,
    label: iconOnly ? (
      <span title={merged[mode]} aria-label={merged[mode]} style={{ display: 'inline-flex', alignItems: 'center' }}>
        {ICONS[mode]}
      </span>
    ) : (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {ICONS[mode]}
        <span>{merged[mode]}</span>
      </span>
    )
  }));

  return (
    <SegmentedControl
      data={data}
      value={value}
      onChange={(v) => setThemeMode(v as ThemeMode)}
      size={size ?? 'xs'}
      fullWidth={fullWidth}
      {...rest}
    />
  );
});
